/**
 * Round 218A Phase 8D-2 (2026-02-05) — Client-side generation
 * attempt-id persistence.
 *
 * The Phase 8D-1B backend requires an `attempt_id` on every
 * Business Credit production request for HTTP-level deduplication.
 * This hook provides a STABLE attempt_id that survives:
 *
 *   • loading states
 *   • accidental double-clicks
 *   • network retries
 *   • insufficient-credit interruption + Stripe purchase redirect
 *   • Continue/resume after purchase
 *
 * A new attempt_id is created ONLY when the customer deliberately
 * initiates a new Generate/Retry action after a refunded failure or
 * a successful completion — never merely because the component
 * rerenders or the browser refreshes.
 *
 * Persistence layer: `sessionStorage`, keyed by
 * `paygbot.attempt.{scopeKey}`. The scopeKey is typically the
 * (asset_kind + project id) tuple so different in-flight projects
 * don't share IDs.
 *
 * API:
 *   const {
 *     attemptId,           // stable string
 *     resetAttemptId,      // start a fresh attempt
 *     savePendingIntent,   // stash resume metadata before Stripe redirect
 *     consumePendingIntent // read + clear the resume metadata
 *   } = useAttemptId(scopeKey);
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'paygbot.attempt.';
const PENDING_KEY = 'paygbot.pending_generation_intent';


function _uuid() {
  // RFC-4122 v4 without depending on `crypto.randomUUID` (older
  // Safari + PWA WebViews still lack it).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}


function _read(scopeKey) {
  try {
    return window.sessionStorage.getItem(STORAGE_PREFIX + scopeKey) || '';
  } catch {
    return '';
  }
}


function _write(scopeKey, value) {
  try {
    if (value) {
      window.sessionStorage.setItem(STORAGE_PREFIX + scopeKey, value);
    } else {
      window.sessionStorage.removeItem(STORAGE_PREFIX + scopeKey);
    }
  } catch {
    /* private mode / SSR — no-op */
  }
}


export default function useAttemptId(scopeKey) {
  // Compute lazily on mount so we don't touch sessionStorage during
  // SSR / build.
  const [attemptId, setAttemptId] = useState(() => {
    if (!scopeKey) return '';
    const existing = _read(scopeKey);
    if (existing) return existing;
    const fresh = _uuid();
    _write(scopeKey, fresh);
    return fresh;
  });

  useEffect(() => {
    if (!scopeKey) return;
    if (attemptId) return;
    const existing = _read(scopeKey);
    if (existing) {
      setAttemptId(existing);
    } else {
      const fresh = _uuid();
      _write(scopeKey, fresh);
      setAttemptId(fresh);
    }
  }, [scopeKey, attemptId]);

  const resetAttemptId = useCallback(() => {
    const fresh = _uuid();
    if (scopeKey) _write(scopeKey, fresh);
    setAttemptId(fresh);
    return fresh;
  }, [scopeKey]);

  const savePendingIntent = useCallback((intent) => {
    try {
      const payload = {
        ...intent,
        attempt_id: attemptId,
        scope_key: scopeKey,
        saved_at: new Date().toISOString(),
      };
      window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
    } catch {
      /* no-op */
    }
  }, [attemptId, scopeKey]);

  return {
    attemptId,
    resetAttemptId,
    savePendingIntent,
  };
}


// Static helpers so the Settings return-from-checkout handler can
// read the pending intent without needing to mount the hook.
export function consumePendingIntent() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}


export function peekPendingIntent() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
